import { ajaxActions } from './AjaxActions';
import { ConstantS } from './ConstantS';
import { DocumentDto, DocumentTemplateDto, PositionDto, PositionGroupDTO } from './DocumentT';
import { MixpanelS } from './MixpanelS';
import { ToastS } from './ToastS';

const BASE_URL = `${process.env.REACT_APP_DOCUMENT_SERVICE_URL}/position-groups`;

const create = async (documentId: number, index?: number, name?: string): Promise<PositionGroupDTO[] | null> => {
  const searchParams = new URLSearchParams({ documentId: documentId.toString() });
  if (index !== undefined) searchParams.set('index', index.toString());
  if (name) searchParams.set('name', name);
  const res = await ajaxActions.post(`${BASE_URL}?${searchParams}`);
  if (res.status === 201) {
    ToastS.info('gruppe-erstellen', 'Gruppe erstellt');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const createForTemplate = async (
  templateId: number,
  index?: number,
  name?: string,
): Promise<PositionGroupDTO[] | null> => {
  const searchParams = new URLSearchParams({ templateId: templateId.toString() });
  if (index !== undefined) searchParams.set('index', index.toString());
  if (name) searchParams.set('name', name);
  const res = await ajaxActions.post(`${BASE_URL}/template?${searchParams}`);
  if (res.status === 201) {
    ToastS.info('gruppe-erstellen', 'Gruppe erstellt');
    MixpanelS.track(ConstantS.TrackingEvents.PositionGroupCreated);
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const edit = async (id: string, patch: Partial<PositionGroupDTO>): Promise<PositionGroupDTO | null> => {
  const res = await ajaxActions.patch(`${BASE_URL}/${id}`, patch);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const move = async (documentId: number, from: number, to: number): Promise<DocumentDto | null> => {
  const searchParams = new URLSearchParams({ documentId, from, to } as unknown as Record<string, string>);
  const res = await ajaxActions.patch(`${BASE_URL}/move?${searchParams}`, null);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const moveTemplate = async (templateId: number, from: number, to: number): Promise<DocumentTemplateDto | null> => {
  const searchParams = new URLSearchParams({ templateId, from, to } as unknown as Record<string, string>);
  const res = await ajaxActions.patch(`${BASE_URL}/move/template?${searchParams}`, null);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const remove = async (id: string, documentId: number, removePositions: boolean): Promise<DocumentDto | null> => {
  const searchParams = new URLSearchParams({
    documentId: documentId.toString(),
    removePositions: removePositions.toString(),
  });
  const res = await ajaxActions.del(`${BASE_URL}/${id}?${searchParams}`);
  if (res.ok) {
    ToastS.info('grouppe-geloscht', 'Gruppe gelöscht');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const removeFromTemplate = async (
  id: string,
  templateId: number,
  removePositions: boolean,
): Promise<DocumentTemplateDto | null> => {
  const searchParams = new URLSearchParams({
    templateId: templateId.toString(),
    removePositions: removePositions.toString(),
  });
  const res = await ajaxActions.del(`${BASE_URL}/${id}/template?${searchParams}`);
  if (res.ok) {
    ToastS.info('grouppe-geloscht', 'Gruppe gelöscht');
    MixpanelS.track(ConstantS.TrackingEvents.PositionGroupDeleted);
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertPosition = async (
  groupId: string,
  positionId: number,
  documentId: number,
  targetPositionId?: number,
): Promise<DocumentDto | null> => {
  const res = await ajaxActions.put(
    `${BASE_URL}/${groupId}/positions/${positionId}?documentId=${documentId}${
      targetPositionId ? `&targetPositionId=${targetPositionId}` : ''
    }`,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertPositionTemplate = async (
  groupId: string,
  positionId: number,
  templateId: number,
  targetPositionId?: number,
): Promise<DocumentTemplateDto | null> => {
  const res = await ajaxActions.put(
    `${BASE_URL}/${groupId}/positions/${positionId}/template?templateId=${templateId}${
      targetPositionId ? `&targetPositionId=${targetPositionId}` : ''
    }`,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertNewPosition = async (
  groupId: string,
  documentId: number,
  targetPositionId?: number,
  product?: PositionDto,
): Promise<DocumentDto | null> => {
  const res = await ajaxActions.post(
    `${BASE_URL}/${groupId}/positions?documentId=${documentId}${
      targetPositionId ? `&targetPositionId=${targetPositionId}` : ''
    }`,
    product,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertNewPositionTemplate = async (
  groupId: string,
  templateId: number,
  targetPositionId?: number,
  product?: PositionDto,
): Promise<DocumentTemplateDto | null> => {
  const res = await ajaxActions.post(
    `${BASE_URL}/${groupId}/positions/template?templateId=${templateId}${
      targetPositionId ? `&targetPositionId=${targetPositionId}` : ''
    }`,
    product,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const deletePosition = async (positionId: number, documentId: number): Promise<DocumentDto | null> => {
  const res = await ajaxActions.del(`${BASE_URL}/positions/${positionId}?documentId=${documentId}`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const deletePositionTemplate = async (positionId: number, templateId: number): Promise<DocumentTemplateDto | null> => {
  const res = await ajaxActions.del(`${BASE_URL}/positions/${positionId}/template?templateId=${templateId}`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

// eslint-disable-next-line import/prefer-default-export
export const PositionGroupS = {
  create,
  createForTemplate,
  edit,
  move,
  moveTemplate,
  remove,
  removeFromTemplate,
  insertPosition,
  insertPositionTemplate,
  insertNewPosition,
  insertNewPositionTemplate,
  deletePosition,
  deletePositionTemplate,
};
